import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { fileNameFromDisposition, readBlobError } from '@/services/order-samples.service';
import type {
  ApiResponse,
  PulseImport,
  PulseImportCheck,
  PulseImportRequest,
  PulseImportStarted,
  PulseOnboardingResult
} from '@/types';

export const pulseOnboardingService = {
  /**
   * Start the Pulse ticket import for an account.
   *
   * There is no matching read. The import's progress lives in Pulse's own
   * `onboarding_status`, which this app does not surface, so the only thing to show is
   * what this call answered.
   */
  async start(accountId: number): Promise<PulseOnboardingResult> {
    const res = await api.post<ApiResponse<PulseOnboardingResult>>(
      API_ENDPOINTS.PULSE_ONBOARDING.START(accountId)
    );
    return res.data.data;
  }
};

/**
 * The Import tickets sheet: count, start, follow and read back a Pulse import. jobs runs the
 * import; machine relays and audits who started it.
 */
export const pulseImportService = {
  /** The counts for a request, before anything is started. */
  async check(accountId: number, request: PulseImportRequest): Promise<PulseImportCheck> {
    const res = await api.post<ApiResponse<PulseImportCheck>>(
      API_ENDPOINTS.PULSE_IMPORTS.CHECK(accountId),
      request
    );
    return res.data.data;
  },

  async start(accountId: number, request: PulseImportRequest): Promise<PulseImportStarted> {
    const res = await api.post<ApiResponse<PulseImportStarted>>(
      API_ENDPOINTS.PULSE_IMPORTS.BASE(accountId),
      request
    );
    return res.data.data;
  },

  /** The account's imports, newest first. */
  async list(accountId: number): Promise<PulseImport[]> {
    const res = await api.get<ApiResponse<{ imports: PulseImport[] }>>(
      API_ENDPOINTS.PULSE_IMPORTS.BASE(accountId)
    );
    return res.data.data.imports;
  },

  /** What became of each ticket, as a CSV file. */
  async results(accountId: number, importId: string): Promise<{ blob: Blob; fileName: string }> {
    try {
      const res = await api.get<Blob>(API_ENDPOINTS.PULSE_IMPORTS.RESULTS(accountId, importId), {
        responseType: 'blob'
      });
      const fileName =
        fileNameFromDisposition(res.headers?.['content-disposition']) ?? `pulse-import-${importId}.csv`;
      return { blob: res.data, fileName };
    } catch (error) {
      throw await readBlobError(error, "Couldn't download the results.");
    }
  }
};
